import { useCallback, useContext, useEffect, useState } from "react";
import {
  MdAlternateEmail,
  MdBadge,
  MdClose,
  MdLock,
  MdLogin,
  MdPerson,
  MdPersonAdd,
  MdPhone,
} from "react-icons/md";
import { AppContext } from "../App";

export default function MembershipAuthModal(props) {
  const {
    show,
    onClose,
    selectedTypeLabel = "membership",
    variant = "auth",
  } = props;
  const appContext = useContext(AppContext);

  const [mode, setMode] = useState("login");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  function resetForm() {
    setEmail("");
    setFirstname("");
    setLastname("");
    setPhone("");
    setPassword("");
    setConfirmPassword("");
    setMode("login");
  }

  useEffect(() => {
    if (show) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [show]);

  const handleClose = useCallback(() => {
    if (loading) {
      return;
    }

    resetForm();
    onClose();
  }, [loading, onClose]);

  useEffect(() => {
    if (!show) {
      return;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [show, handleClose]);

  async function handleSubmit(event) {
    event.preventDefault();

    if (!email || !password || loading) {
      return;
    }

    if (mode === "register" && password !== confirmPassword) {
      appContext.tellError("Passwords do not match.");
      return;
    }

    setLoading(true);

    let success = false;

    if (mode === "login") {
      success = await appContext.loginMember({ email, password });
    } else {
      success = await appContext.registerMember({
        firstname,
        lastname,
        email,
        phone,
        password,
      });
    }

    setLoading(false);

    if (success) {
      appContext.tellMessage(
        mode === "login"
          ? "Login successful. You can now apply for membership."
          : "Account created successfully.",
      );
      handleClose();
    } else {
      appContext.tellError(
        mode === "login"
          ? "Login failed. Please check your email and password."
          : "Registration failed. Please try again with different credentials.",
      );
    }
  }

  if (!show) {
    return null;
  }

  return (
    <div
      className="newsletter-modal-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        className="newsletter-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="membership-auth-title"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        <div className="newsletter-modal-card">
          <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
            <div>
              <span className="newsletter-modal-badge">
                {variant === "honorary"
                  ? "Honorary Membership"
                  : "Membership Access"}
              </span>
              <h4
                id="membership-auth-title"
                className="font-bold text-primary mt-3 mb-2"
              >
                {variant === "honorary"
                  ? "How honorary membership is attained"
                  : "Login to apply for membership"}
              </h4>
              <p className="mb-0 newsletter-modal-copy">
                {variant === "honorary"
                  ? "Honorary membership is not applied for through the standard portal. It is granted at the discretion of the General Meeting to people whose service, contribution, or professional support has clearly advanced TEF and its mission, including distinguished contributors and media educators. This category is honorary in nature, so it does not carry voting or elective rights."
                  : `Continue your ${selectedTypeLabel} application by signing in with your email and password. If you do not have an account yet, use the register option below.`}
              </p>
            </div>

            <button
              type="button"
              className="btn btn-light newsletter-modal-close"
              onClick={handleClose}
              aria-label="Close membership login form"
            >
              <MdClose size={20} />
            </button>
          </div>

          {variant === "auth" ? (
            <>
              <div className="d-flex gap-2 mb-4 flex-wrap">
                <button
                  type="button"
                  className={`btn btn-sm ${
                    mode === "login" ? "btn-primary" : "btn-outline-primary"
                  } membership-auth-switch`}
                  onClick={() => setMode("login")}
                  disabled={loading}
                >
                  <MdLogin className="me-2" />
                  Login
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    mode === "register"
                      ? "btn-secondary"
                      : "btn-outline-secondary"
                  } membership-auth-switch`}
                  onClick={() => setMode("register")}
                  disabled={loading}
                >
                  <MdPersonAdd className="me-2" />
                  Register
                </button>
              </div>

              <form onSubmit={handleSubmit}>
            <div className="row g-3">
              {mode === "register" ? (
                <div className="col-md-6">
                  <label className="mb-2">First Name</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <MdPerson />
                    </span>
                    <input
                      type="text"
                      value={firstname}
                      onChange={(event) => setFirstname(event.target.value)}
                      className="form-control newsletter-modal-input"
                      placeholder="Enter your first name"
                      required
                    />
                  </div>
                </div>
              ) : null}

              {mode === "register" ? (
                <div className="col-md-6">
                  <label className="mb-2">Last Name</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <MdPerson />
                    </span>
                    <input
                      type="text"
                      value={lastname}
                      onChange={(event) => setLastname(event.target.value)}
                      className="form-control newsletter-modal-input"
                      placeholder="Enter your last name"
                      required
                    />
                  </div>
                </div>
              ) : null}

              <div className="col-12">
                <label className="mb-2">Email address</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <MdAlternateEmail />
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="form-control newsletter-modal-input"
                    placeholder="Enter your email address"
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              {mode === "register" ? (
                <div className="col-12">
                  <label className="mb-2">Phone number</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <MdPhone />
                    </span>
                    <input
                      type="text"
                      value={phone}
                      onChange={(event) => setPhone(event.target.value)}
                      className="form-control newsletter-modal-input"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>
              ) : null}

              <div className={mode === "register" ? "col-md-6" : "col-12"}>
                <label className="mb-2">Password</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <MdLock />
                  </span>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="form-control newsletter-modal-input"
                    placeholder="Enter your password"
                    autoComplete={
                      mode === "login" ? "current-password" : "new-password"
                    }
                    required
                  />
                </div>
              </div>

              {mode === "register" ? (
                <div className="col-md-6">
                  <label className="mb-2">Confirm password</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <MdLock />
                    </span>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(event) =>
                        setConfirmPassword(event.target.value)
                      }
                      className="form-control newsletter-modal-input"
                      placeholder="Repeat your password"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="d-flex flex-column flex-sm-row gap-2 mt-4">
              <button
                type="submit"
                className="btn btn-secondary btn-padded"
                disabled={loading}
              >
                {loading
                  ? mode === "login"
                    ? "Signing in..."
                    : "Creating account..."
                  : mode === "login"
                    ? "Login to apply"
                    : "Create account"}
              </button>

              <button
                type="button"
                className="btn btn-outline-primary btn-padded"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
              </form>
            </>
          ) : (
            <div className="mt-4">
              <div className="newsletter-feature-item mb-3">
                <h6 className="font-semi-bold mb-2">Important note</h6>
                <p className="mb-0 text-muted">
                  Honorary membership is a recognition-based category rather than
                  a self-application path, so nominations and appointments are
                  considered internally through TEF&apos;s formal decision-making
                  process.
                </p>
              </div>

              <button
                type="button"
                className="btn btn-outline-primary btn-padded"
                onClick={handleClose}
              >
                Understood
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
